import i18next from 'i18next';
import type { OptionalDTO } from 'src/api';
import type { VehicleEntity } from 'src/api';
import { NOLEGGIATORI_QUOTATORE } from 'src/utils/constants';
import { isInvalidNumber, arrayToOptions } from 'src/utils';
import { cloneDeep } from 'lodash';

export interface SelectOptions {
	value: string;
	label: string;
}

export type OrdersMode = 'multi' | 'single';

export const listaNoleggiatoriQuotatore = [
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.arval.name, is_checked: true, extra_sconto_perc: null },
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.ayvens.name, is_checked: true, extra_sconto_perc: null },
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.leasys.name, is_checked: true, extra_sconto_perc: null },
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.athlon.name, is_checked: true, extra_sconto_perc: null },
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.alphabet.name, is_checked: true, extra_sconto_perc: null },
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.drivalia.name, is_checked: true, extra_sconto_perc: null },
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.kinto.name, is_checked: false, extra_sconto_perc: null },
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.vw_leasing.name, is_checked: true, extra_sconto_perc: null },
	{ noleggiatore: NOLEGGIATORI_QUOTATORE.hyundai_rent.name, is_checked: true, extra_sconto_perc: null },
];

export const salableByOptions: SelectOptions[] = [
	{ value: 'MASERATI', label: 'MASERATI' },
	{ value: 'VOLKSWAGEN', label: 'VOLKSWAGEN' },
	{ value: 'STELLANTIS', label: 'STELLANTIS' },
];

export const emptyInstanceValues = () => ({
	dealer: '',
	settimana_prod: '',
	data_disp: '',
	city: '',
	data_telaio: '',
	telaio: '',
	data_immatricolazione: '',
	targa: '',
	id_bo: '',
	giorno_disp_horizon: '',
	promo: false,
	promo_note: '',
	note: '',
	codice_colore_esterno: '',
	codice_colore_interno: '',
	colore_esterno: '',
	colore_interno: '',
	optionals: [] as OptionalDTO[],
	nr_ordine_list: [] as string[],
	document: [] as any[],
	lista_noleggiatori_quotatore: listaNoleggiatoriQuotatore.map((n) => ({ ...n })),
	extra_sconto_perc: 0,
	visibility_tag: '',
	limited_visibility: [] as SelectOptions[],
});

export const mapLimitedVisibility = (list: SelectOptions[] | undefined): string[] =>
	(list || []).map((i) => i.value);

export const validateInstance = (
	values: any,
	{
		ordersMode,
		interiorColorRequired = true,
	}: { ordersMode: OrdersMode; interiorColorRequired?: boolean }
): Record<string, string> => {
	const errors: Record<string, string> = {};
	const REQUIRED = i18next.t('mandatory');

	if (isInvalidNumber(values.extra_sconto_perc)) {
		errors.extra_sconto_perc = i18next.t('numeric_values_allowed');
	}
	(values.lista_noleggiatori_quotatore || []).forEach((item: any, idx: number) => {
		if (isInvalidNumber(item?.extra_sconto_perc)) {
			errors[`lista_noleggiatori_quotatore.${idx}.extra_sconto_perc`] =
				i18next.t('numeric_values_allowed');
		}
	});
	if (!values.dealer) errors.dealer = REQUIRED;
	if (!values.codice_costruttore) errors.codice_costruttore = REQUIRED;
	if (!values.codice_colore_esterno) errors.codice_colore_esterno = REQUIRED;
	if (interiorColorRequired && !values.codice_colore_interno)
		errors.codice_colore_interno = REQUIRED;
	if (ordersMode === 'multi') {
		if (!values.nr_ordine_list?.length) errors.nr_ordine_list = REQUIRED;
	} else {
		if (!values.nr_ordine) errors.nr_ordine = REQUIRED;
	}
	return errors;
};

export const newOptional: OptionalDTO = {
	codice_costruttore: '',
	descrizione: '',
	valore: 0,
};

export const storedVehicleToFormValues = (res: VehicleEntity): Record<string, any> => {
	const det: any = res.dettagli_veicolo ?? {};
	const dati: any = res.dati_veicolo ?? {};

	const merged = listaNoleggiatoriQuotatore.map((n) => ({ ...n }));
	(res.lista_noleggiatori_quotatore || []).forEach((stored: any) => {
		const row = merged.find((m) => m.noleggiatore === stored.noleggiatore);
		if (row) {
			row.is_checked = stored.is_checked;
			row.extra_sconto_perc = stored.extra_sconto_perc;
		}
	});

	const limited_visibility = det.limited_visibility?.length
		? arrayToOptions(det.limited_visibility)
		: [];

	return {
		...emptyInstanceValues(),
		dealer: det.dealer ?? '',
		codice_costruttore: det.codice_costruttore ?? '',
		settimana_prod: det.settimana_prod ?? '',
		data_disp: det.data_disp ?? '',
		city: det.city ?? '',
		data_telaio: det.data_telaio ?? '',
		telaio: det.telaio ?? '',
		data_immatricolazione: det.data_immatricolazione ?? '',
		targa: det.targa ?? '',
		id_bo: det.id_bo ?? '',
		giorno_disp_horizon: det.giorno_disp ?? '',
		promo: det.promo ?? false,
		promo_note: det.promo_note ?? '',
		note: det.note ?? '',
		codice_colore_esterno: det.codice_colore_esterno ?? '',
		codice_colore_interno: det.codice_colore_interno ?? '',
		colore_esterno: det.colore_esterno ?? '',
		colore_interno: det.colore_interno ?? '',
		document: det.document?.length ? det.document : [],
		nr_ordine: det.nr_ordine ?? '',
		optionals: res.optionals?.length ? cloneDeep(res.optionals) : [{ ...newOptional }],
		stato_corrente: res.stato_corrente,
		prezzo_listino: dati.prezzo_listino,
		extra_sconto_perc: dati.extra_sconto_perc,
		jato_uid: dati.jato_uid,
		lista_noleggiatori_quotatore: merged,
		limited_visibility,
		codice_motornet: dati.codice_motornet ?? '',
		codice_eurotax: dati.codice_eurotax ?? '',
	};
};
